import type { HTMLAttributes } from "react";
import { Eye, GripVertical, RefreshCw } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { contentTypeLabel, type TemplateKind } from "@/lib/composerTemplates";
import { cn } from "@/lib/utils";

type TemplateCardProps = {
  name: string;
  description?: string | null;
  contentType: string;
  /** "Static" or "Dynamic" — drives the primary action label and tooltip. */
  kind: TemplateKind;
  badge?: string;
  /** Static templates: insert structure into Draft Canvas. */
  onPushToCanvas?: () => void;
  /** Dynamic templates: pre-fill intake / open wizard. */
  onUse?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onClone?: () => void;
  onPreview?: () => void;
  onUpdateFromDraft?: () => void;
  updateFromDraftDisabledReason?: string;
  selected?: boolean;
  dragHandleProps?: HTMLAttributes<HTMLButtonElement>;
};

export const TemplateCard = ({
  name,
  description,
  contentType,
  kind,
  badge,
  onUse,
  onPushToCanvas,
  onEdit,
  onDelete,
  onClone,
  onPreview,
  onUpdateFromDraft,
  updateFromDraftDisabledReason,
  selected,
  dragHandleProps,
}: TemplateCardProps) => {
  const isDynamic = kind === "dynamic";
  const primaryHandler = isDynamic ? (onUse ?? onPushToCanvas) : (onPushToCanvas ?? onUse);
  const primaryLabel = isDynamic ? "Start intake" : "Insert into Draft";
  const primaryTooltip = isDynamic
    ? "Answer a few content-specific questions, then the Planning Agent opens in chat with your answers pre-loaded."
    : "Drops this template's structure into the Draft Canvas. No AI, no questions.";

  return (
    <Card className={cn("flex flex-col gap-2 p-4", selected && "ring-2 ring-primary")}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 flex-1 items-start gap-2">
          {dragHandleProps && (
            <button
              type="button"
              aria-label="Drag to reorder"
              className="mt-0.5 cursor-grab touch-none text-muted-foreground hover:text-foreground"
              {...dragHandleProps}
            >
              <GripVertical className="h-4 w-4" />
            </button>
          )}
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="truncate text-sm font-semibold">{name}</h3>
              <Badge variant="outline" className="text-[10px] uppercase tracking-wider">
                {isDynamic ? "Dynamic" : "Static"}
              </Badge>
              {badge && (
                <Badge variant="outline" className="text-[10px] uppercase tracking-wider">
                  {badge}
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground">{contentTypeLabel(contentType)}</p>
          </div>
        </div>
      </div>
      {description && <p className="line-clamp-2 text-xs text-muted-foreground">{description}</p>}
      <div className="mt-1 flex flex-wrap gap-2">
        {primaryHandler && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button size="sm" onClick={primaryHandler}>
                {primaryLabel}
              </Button>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">{primaryTooltip}</TooltipContent>
          </Tooltip>
        )}
        {onPreview && (
          <Button size="sm" variant="outline" onClick={onPreview}>
            <Eye className="h-3.5 w-3.5" />
            Preview
          </Button>
        )}
        {onClone && (
          <Button size="sm" variant="outline" onClick={onClone}>
            Save a copy
          </Button>
        )}
        {onUpdateFromDraft && (
          <Button
            size="sm"
            variant="outline"
            onClick={onUpdateFromDraft}
            disabled={Boolean(updateFromDraftDisabledReason)}
            title={updateFromDraftDisabledReason}
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Update from Draft
          </Button>
        )}
        {onEdit && (
          <Button size="sm" variant="outline" onClick={onEdit}>
            Edit
          </Button>
        )}
        {onDelete && (
          <Button size="sm" variant="ghost" onClick={onDelete}>
            Delete
          </Button>
        )}
      </div>
    </Card>
  );
};
